import { Grid } from '@mui/material';
import Box from '@mui/material/Box';
import { businessData as businessDataAtom } from 'atoms';
import { ViewInvoiceDetail as ViewInvoiceDetailAtom } from 'atoms';
import Button from 'common/Button';
import ButtonSecondary from 'common/ButtonSecondary';
import FormHeading from 'common/FormHeading';
import FormModal from 'common/FormModal';
import FormStyle from 'common/FormStyle';
import StickyActionFooter from 'common/StickyActionFooter';
import Subtext from 'common/Subtext';
import { FormatTime } from 'common/formatters';
import moment from 'moment';
import StatusBadge from 'common/StatusBadge';
import ButtonLoader from 'common/Loaders/ButtonLoader';
import routes from 'config/routes';
import PaymentRequest from 'emails/PaymentRequest';
import PaymentComplete from 'emails/PaymentComplete';
import { useInvoiceDetails } from 'hooks/useInvoiceDetails';
import ManualPaymentForm from './Partials/ManualPaymentForm';
import Form from './Partials/Form';
import ServicePopup from 'pages/Business/Service/ServicePopup';
import ReactDOMServer from 'react-dom/server';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { getProviderServicesApi } from 'SupabseHandler';
import {  generateUUID, sendEmail, sendSmsMessage } from 'utils/methods';
import { useAuth } from 'context/AuthContext';
import ConfirmDeleteInvoiceModal from 'common/ConfirmDeleteInvoiceModal';

import { Elements, useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { getStripePromise, connectedAccountIdForBusiness } from 'utils/stripePromise';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { calculateFees } from 'utils/calculateFees';
import { toasterNotification } from 'utils/toaster';


// Manual Payment Modal Component
const ManualPaymentModal = ({
    open,
    onClose,
    invoiceDetail,
    formData,
    isMakingPayment,
    setIsMakingPayment,
    manualPaymentAuthorized,
    setManualPaymentAuthorized,
    onPaymentConfirmed,
    completePaymentAndNotify,
    cleanPrice
}) => {
    const manualPaymentFormRef = useRef(null);
    const [cardComplete, setCardComplete] = useState(false);
    const [cardError, setCardError] = useState('');

    // Standard providers use direct charges, so Stripe.js must be bound to the
    // connected account for client-side confirmation of a new card.
    const business = useRecoilValue(businessDataAtom);
    const connectedAccountId = connectedAccountIdForBusiness(business);
    const stripePromise = useMemo(
        () => getStripePromise(connectedAccountId),
        [connectedAccountId],
    );

    const handlePay = async () => {
        if (manualPaymentFormRef.current?.handlePay) {
            await manualPaymentFormRef.current.handlePay();
        } else {
            console.error('ManualPaymentModal: handlePay method not available on ref');
        }
    };

    const payLabel = `Pay $${Number(formData.servicePrice.reduce((acc, price) => parseFloat(acc) + parseFloat(cleanPrice(price)), 0)).toFixed(2)}`;

    // Update card completion state when form ref changes
    useEffect(() => {
        const interval = setInterval(() => {
            if (manualPaymentFormRef.current) {
                const isComplete = manualPaymentFormRef.current.isCardComplete;
                const error = manualPaymentFormRef.current.cardError;
                setCardComplete(isComplete);
                if (error) setCardError(error);
            }
        }, 100);
        return () => clearInterval(interval);
    }, []);

    return (
        <FormModal 
            open={open} 
            onClose={onClose} 
            title={'Enter Payment Details'}
            subText={`To pay manually for ${formData.patientName}'s pending invoice.`}
            stickyFooter={
                <StickyActionFooter
                    onCancel={onClose}
                    onSave={handlePay}
                    cancelText="Cancel"
                    saveText={payLabel}
                    isLoading={isMakingPayment}
                    disabled={!cardComplete || !manualPaymentAuthorized || isMakingPayment}
                />
            }
        >
            <Elements stripe={stripePromise}>
                <ManualPaymentForm
                    ref={manualPaymentFormRef}
                    invoiceId={invoiceDetail?.id}
                    isMakingPayment={isMakingPayment}
                    setIsMakingPayment={setIsMakingPayment}
                    onPaymentConfirmed={onPaymentConfirmed}
                    isAuthorized={manualPaymentAuthorized}
                    patientName={formData.patientName}
                    onAuthorizationChange={setManualPaymentAuthorized}
                    patientId={formData.patient_id}
                />
            </Elements>
        </FormModal>
    );
};

// Card Elements component 
const InlineCardElements = ({ disabled = false, onCompleteChange, savedCardData = null }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [cardError, setCardError] = useState('');
    const [numberComplete, setNumberComplete] = useState(false);
    const [expiryComplete, setExpiryComplete] = useState(false);
    const [cvcComplete, setCvcComplete] = useState(false);

    const baseStyle = {
        base: {
            fontSize: '16px',
            color: '#1f2937',
            '::placeholder': { color: '#9ca3af' },
        },
        invalid: {
            color: '#ef4444',
        },
    };

    const onChange = (event) => {
        if (event?.error) {
            setCardError(event.error.message || '');
        } else {
            setCardError('');
        }
        if (event?.elementType === 'cardNumber') setNumberComplete(!!event.complete);
        if (event?.elementType === 'cardExpiry') setExpiryComplete(!!event.complete);
        if (event?.elementType === 'cardCvc') setCvcComplete(!!event.complete);
    };

    React.useEffect(() => {
        if (typeof onCompleteChange === 'function') {
            onCompleteChange(numberComplete && expiryComplete && cvcComplete);
        }
    }, [numberComplete, expiryComplete, cvcComplete]);

    const cardElement = elements?.getElement(CardNumberElement);
    if (!disabled && cardElement) {
        // Ensure the element is enabled if previously disabled
        try { cardElement.update({ disabled: false }); } catch (e) {}
    };

    return (
        <div className="grid grid-cols-1 gap-4">
            <div className="common-input p-2.5 px-5">
                <CardNumberElement
                    options={{ 
                        style: baseStyle, 
                        disabled, 
                        placeholder: 'Card number' 
                    }}
                    onChange={onChange}
                />
            </div>
            <div className="flex gap-4">
                <div className="common-input p-2.5 px-5 w-1/2">
                    <CardExpiryElement
                        options={{ 
                            style: baseStyle, 
                            disabled, 
                            placeholder: 'MM / YY' 
                        }}
                        onChange={onChange}
                    />
                </div>
                <div className="common-input p-2.5 px-5 w-1/2">
                    <CardCvcElement
                        options={{ 
                            style: baseStyle, 
                            disabled, 
                            placeholder: 'CVC' 
                        }}
                        onChange={onChange}
                    />
                </div>
            </div>
            {cardError ? <div className="text-red-500 text-sm">{cardError}</div> : null}
        </div>
    );
};

const StripeContextBridge = ({ onReady }) => {
    const stripe = useStripe();
    const elements = useElements();
    useEffect(() => {
        if (stripe && elements && typeof onReady === 'function') {
            onReady({ stripe, elements });
        }
    }, [stripe, elements]);
    return null;
};

// Bridge component to expose Stripe context to parent via callback
// (definition consolidated above)

const CardElementsAndPayButton = ({ invoiceId, isMakingPayment, setIsMakingPayment, onPaymentConfirmed, isAuthorized = true, onCancel, payLabel = 'Pay', savedCardData = null, showDelete = false, onDelete, deleteText = 'Delete Invoice' }) => {
    const { token } = useAuth();
    const stripeJs = useStripe();
    
    const elements = useElements();
    const [isCardComplete, setIsCardComplete] = useState(false);
    const business = useRecoilValue(businessDataAtom);

    const handlePay = async () => {
        
        if (!stripeJs) {
            console.error('CardElementsAndPayButton: Stripe not loaded');
            return;
        }
        
        if (!elements) {
            console.error('CardElementsAndPayButton: Elements not loaded');
            return;
        }
        
        if (!isCardComplete) {
            console.error('CardElementsAndPayButton: Card not complete');
            return;
        }
        
        try {
            setIsMakingPayment(true);

            if (!token) {
                console.error('CardElementsAndPayButton: No authentication token found');
                setIsMakingPayment(false);
                return;
            }
            
            
            const invoiceResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/invoice/${invoiceId}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!invoiceResponse.ok) {
                throw new Error('Failed to fetch invoice');
            }

            const invoiceResult = await invoiceResponse.json();
            // Backend may return { success, data } or the data directly
            const invData = invoiceResult?.success 
                ? invoiceResult.data[0]:null;

            if (!invData) {
                throw new Error('Invoice not found');
            }

            const services = Array.isArray(invData?.service_data) ? invData.service_data : [];
            const totalAmount = services.reduce((sum, service) => sum + parseFloat(service?.price ?? 0), 0);
            const { platformFee } = calculateFees(totalAmount, business?.fee_rate);
            const providerStripeId = business?.stripe_id || null;
            
            const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/create-payment-intent`, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                  },
                body: JSON.stringify({
                    amount: totalAmount,
                    platformFee,
                    providerStripeId,
                    email: invData?.info_source_data || '',
                    patient_name:invData?.patient_name,
                    business_name:business?.name,
                    appointment_id:invData?.appointment_id,
                    provider_id:invData?.provider_id,
                    serviceType:invData?.service_data?.map(service => service.name).join(", "),
                }),
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => null);
                console.error('CardElementsAndPayButton: Payment intent creation failed', errorData);
                throw new Error(errorData?.error || 'Failed to create payment intent');
            }

            const resData = await res.json();
            const clientSecret = resData?.data?.clientSecret;

            const cardElement = elements.getElement(CardNumberElement);
            if (!cardElement) {
                throw new Error('Card element not found');
            }
            
            const result = await stripeJs.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement,
                },
            });

            if (result.error) {
                const msg =
                    result.error.message ||
                    result.paymentIntent?.last_payment_error?.message ||
                    'Your card was declined. Please try another payment method.';
                toasterNotification('error', '', msg);
                return;
            }

            const pi = result.paymentIntent;
            const okStatus = pi?.status === 'succeeded' || pi?.status === 'processing' || pi?.status === 'requires_capture';
            if (!okStatus) {
                const msg =
                    pi?.last_payment_error?.message ||
                    'Payment was not completed. Please check your card details and try again.';
                toasterNotification('error', '', msg);
                return;
            }

            const intentId = pi?.id;
            if (intentId) {
                await onPaymentConfirmed(intentId);
            }
        } catch (e) {
            toasterNotification('error', '', e?.message || 'Payment failed. Please try again.');
        } finally {
            setIsMakingPayment(false);
        }
    };

    return (
        <>
            <InlineCardElements disabled={false} onCompleteChange={setIsCardComplete} savedCardData={savedCardData} />
            <div className="mt-4">
                <StickyActionFooter
                    showDelete={showDelete}
                    onDelete={onDelete}
                    deleteText={deleteText}
                    onCancel={onCancel}
                    cancelText="Cancel"
                    onSave={handlePay}
                    saveText={payLabel}
                    isLoading={isMakingPayment}
                    disabled={isMakingPayment || !isCardComplete || !isAuthorized}
                    className="border-0"
                    style={{
                        backgroundColor: 'transparent',
                        backdropFilter: 'none',
                        WebkitBackdropFilter: 'none',
                        borderTop: '0',
                        padding: '0',
                    }}
                />
            </div>
        </>
    );
};

// Helper function to remove commas from price strings before parsing
const cleanPrice = (price) => {
    if (typeof price === 'string') {
        return price.replace(/,/g, '');
    }
    return price;
};

const InvoicePopup = ({ open, onClose, getInvoices, manualMode = false, providers = [], servicesList: initialServicesList = [], providerFullDetails = [] }) => {
    const { token, user } = useAuth();
    const [servicesCount, setServicesCount] = useState(1);
    const [openProviderDropdown, setOpenProviderDropdown] = useState(false);
    const [, setProviderEmail] = useInvoiceDetails();
    const [business, setBusiness] = useRecoilState(businessDataAtom);
    // Standard providers use direct charges; bind Stripe.js to the connected account.
    const connectedAccountId = connectedAccountIdForBusiness(business);
    const stripePromise = useMemo(
        () => getStripePromise(connectedAccountId),
        [connectedAccountId],
    );
    const [servicesList, setServicesList] = useState(initialServicesList);

    useEffect(() => {
        if (initialServicesList.length > 0 && servicesList.length === 0) {
            setServicesList(initialServicesList);
        }
    }, [initialServicesList]);

    const [invoiceDetail, setInvoiceDetail] = useRecoilState(ViewInvoiceDetailAtom);
    const [isViewMode, setIsViewMode] = useState(false);
    const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
    const [invoiceStatus, setInvoiceStatus] = useState('');
    const [isResendingEmail, setIsResendingEmail] = useState(false);
    const [isInitiatingPayment, setIsInitiatingPayment] = useState(false);
    const [userAcceptedTerms, setUserAcceptedTerms] = useState(false);
    const [isLoadingCardDetails, setIsLoadingCardDetails] = useState(false);
    const [cardForm, setCardForm] = useState({ cardNumber: '', expiryMonth: '', expiryYear: '', cvv: '' });
    const [isMakingPayment, setIsMakingPayment] = useState(false);
    const [paymentRef, setPaymentRef] = useState({ customerId: '', paymentMethodId: '' });
    const [isManualPaymentOpen, setIsManualPaymentOpen] = useState(false);
    const [isEditingSavedCard, setIsEditingSavedCard] = useState(false);
    const [manualPaymentAuthorized, setManualPaymentAuthorized] = useState(false);
    const [invoiceCreatedAt, setInvoiceCreatedAt] = useState(null);
    const [invoiceLastSentAt, setInvoiceLastSentAt] = useState(null);
    const [originalFormData, setOriginalFormData] = useState(null);
    const [hasFormBeenEdited, setHasFormBeenEdited] = useState(false);
    const isUpdatingRef = useRef(false); // Track if we're currently updating to prevent reload
    // Manual create-and-charge state
    const [isManualCardComplete, setIsManualCardComplete] = useState(false);
    const [manualCreateAuthorized, setManualCreateAuthorized] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [isDeletingInvoice, setIsDeletingInvoice] = useState(false);
    const [openServicePopup, setOpenServicePopup] = useState(false);
    const [currentServiceIndex, setCurrentServiceIndex] = useState(null);

    const handleManualAuthorizationToggle = () => {
        setManualCreateAuthorized((prev) => !prev);
    };

    const handleManualAuthorizationKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleManualAuthorizationToggle();
        }
    };

    const [stripeCtx, setStripeCtx] = useState({ stripe: null, elements: null });

    const navigate = useNavigate();

    // Function to get the closest 15-minute time slot
    const getClosestTimeSlot = () => {
        const now = new Date();
        const minutes = now.getMinutes();
        const hours = now.getHours();
        
        // Round to the nearest 15-minute interval
        const roundedMinutes = Math.round(minutes / 15) * 15;
        
        // Handle hour overflow (e.g., if minutes round to 60)
        let finalHours = hours;
        let finalMinutes = roundedMinutes;
        
        if (roundedMinutes >= 60) {
            finalHours = (hours + 1) % 24;
            finalMinutes = 0;
        }
        
        // Format as HH:mm
        return `${finalHours.toString().padStart(2, '0')}:${finalMinutes.toString().padStart(2, '0')}`;
    };

    const formObject = {
        patientName: '',
        patient_id: null, // Initialize patient_id field
        patientEmail: '',
        dateOfService: new Date().toISOString().substr(0, 10),
        timeOfService: getClosestTimeSlot(),
        provider: business?.id || '', // Auto-select business by default
        isBusiness: true, // Default to business
        sendInvoiceBy: 'email',
        sendInvoiceByInfo: '',
        serviceName: [],
        servicePrice: [],
    };
    const [formData, setFormData] = useState(formObject);

    // Validate that every service name is from the dropdown (no typed custom names)
    const areServicesFromList = () => {
        if (!formData.serviceName || !Array.isArray(formData.serviceName)) return true;
        const allowedNames = new Set((servicesList || []).map((s) => s.name.trim()));
        return formData.serviceName.every((name) => {
            const n = (name || '').toString().trim();
            if (!n) return true;
            return allowedNames.has(n);
        });
    };

    // Function to validate if all mandatory fields are filled
    const isFormValid = () => {
        // Check if services data is properly filled
        const hasValidServices =
            formData.serviceName &&
            formData.servicePrice &&
            formData.serviceName.length > 0 &&
            formData.servicePrice.length > 0 &&
            formData.serviceName.every((name) => name && name.trim() !== '') &&
            formData.servicePrice.every(
                (price) => price !== undefined && price !== null && price !== '' && !isNaN(parseFloat(cleanPrice(price))),
            );

        // Validate contact info based on selected method
        let hasValidContactInfo = false;
        if (formData.sendInvoiceBy === 'text') {
            const digits = (formData.sendInvoiceByInfo || '').replace(/\D/g, '');
            const hasValidPhone = digits.length >= 10; // basic US number check
            const hasValidEmail = !!(formData.patientEmail && formData.patientEmail.includes('@'));
            hasValidContactInfo = hasValidPhone && hasValidEmail;
        } else {
            hasValidContactInfo = !!(formData.sendInvoiceByInfo && formData.sendInvoiceByInfo.includes('@'));
        }

        return (
            // Basic required fields
            formData.patientName &&
            formData.patientName.trim() !== '' &&
            formData.dateOfService &&
            formData.timeOfService &&
            formData.provider &&
            // Valid service information
            hasValidServices &&
            // Valid contact information
            hasValidContactInfo
        );
    };

    const completePaymentAndNotify = async (paymentIntentId, invoiceIdOverride = null) => {
        try {
            const invoiceId = invoiceIdOverride || invoiceDetail?.id;
            
            if (!token) {
                console.error('No authentication token found');
                return;
            }
            
            if (!invoiceId) {
                console.error('No invoice ID found');
                return;
            }
            
            // Update invoice via backend API
            const updateResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/invoices`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        id: invoiceId,
                        isDeleted: false,
                        status: 'paid',
                        payment_id: paymentIntentId,
                        user_accepted_terms: true
                    })
                }
            );

            if (!updateResponse.ok) {
                const errorData = await updateResponse.json().catch(() => ({}));
                console.error('Error updating invoice:', errorData.error || 'Failed to update invoice');
                throw new Error(errorData.error || 'Failed to update invoice');
            }

            const updateResult = await updateResponse.json();
            
            // Handle response - could be object or array
            const data = Array.isArray(updateResult.data) 
                ? (updateResult.data.length > 0 ? updateResult.data[0] : null)
                : (updateResult.data || null);
            
            if (!data) {
                console.error('No invoice data returned from update');
                throw new Error('Failed to update invoice');
            }

            // Update appointment payment status and status to 'booked' if appointment_id exists
            if (data?.appointment_id) {
                try {
                    const appointmentUpdateResponse = await fetch(
                        `${process.env.REACT_APP_SERVER_URL}/api/providers/appointments/${data.appointment_id}`,
                        {
                            method: 'PUT',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                payment_status: 'paid',
                                paid_date: new Date().toISOString(),
                                status: 'booked'
                            })
                        }
                    );

                    if (!appointmentUpdateResponse.ok) {
                        const errorData = await appointmentUpdateResponse.json().catch(() => ({}));
                        console.error('Error updating appointment:', errorData.error || 'Failed to update appointment');
                        // Don't throw error here - invoice is already updated, appointment update failure shouldn't block the flow
                    }
                } catch (error) {
                    console.error('Error updating appointment:', error);
                    // Don't throw error here - invoice is already updated, appointment update failure shouldn't block the flow
                }
            }
            // Get receipt URL from Stripe. For Standard providers the charge lives on the
            // connected account, so scope the receipt lookup to that account.
            const receiptQuery = connectedAccountId ? `?stripeAccountId=${connectedAccountId}` : '';
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/v1/stripe/payment-receipt/${paymentIntentId}${receiptQuery}`,
                {
                  method: "GET",
                  headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                  },
                 // credentials: "include", // important if auth is cookie-based
                }
              );
            
              if (!response.ok) {
                throw new Error("Failed to fetch receipt");
              }
            
              const { receiptUrl } = await response.json();

            const invoiceDetails = {
                patientEmail: data?.info_source_data,
                invoiceTotal: data?.total,
                providerId: data?.provider_id,
            };
            let details = null;
            if (invoiceDetails?.providerId) {
                // Fetch provider data via backend API
                if (!token) {
                    console.error('No authentication token found for provider fetch');
                    throw new Error('No authentication token found');
                }
                
                const providerResponse = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${invoiceDetails.providerId}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );
                
                if (!providerResponse.ok) {
                    const errorData = await providerResponse.json().catch(() => ({}));
                    console.error('Error fetching provider data:', errorData.error || 'Failed to fetch provider');
                    throw new Error(errorData.error || 'Failed to fetch provider data');
                }
                
                const providerResult = await providerResponse.json();
                
                // Handle response - could be object or array
                const providerData = Array.isArray(providerResult.data) 
                    ? (providerResult.data.length > 0 ? providerResult.data[0] : null)
                    : (providerResult.data || null);
                
                if (!providerData) {
                    console.error('No provider data returned from API');
                    throw new Error('Provider data not found');
                }
                
                // Convert to array format to match existing code expectations (details[0] is used later)
                details = [providerData];
            } else {
                // Fetch business data via backend API
                if (!token) {
                    console.error('No authentication token found for business fetch');
                    throw new Error('No authentication token found');
                }
                
                if (!data?.business_id) {
                    console.error('No business ID found in invoice data');
                    throw new Error('Business ID not found');
                }
                
                const businessResponse = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/get-business`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ userid: user?.id })
                    }
                );
                
                if (!businessResponse.ok) {
                    const errorData = await businessResponse.json().catch(() => ({}));
                    console.error('Error fetching business data:', errorData.error || 'Failed to fetch business');
                    throw new Error(errorData.error || 'Failed to fetch business data');
                }
                
                const businessResult = await businessResponse.json();
                
                // Handle response - could be object or array
                const businessData = Array.isArray(businessResult.data) 
                    ? (businessResult.data.length > 0 ? businessResult.data[0] : null)
                    : (businessResult.data || null);
                
                if (!businessData) {
                    console.error('No business data returned from API');
                    throw new Error('Business data not found');
                }
                
                details = {
                    name: businessData.name,
                    email: businessData.email
                };
            }

            const doctorName = data?.provider_id
                ? `${details?.[0]?.first_name} ${details?.[0]?.last_name}`
                : details?.name;
            const emailContent = `
                <!DOCTYPE html>
                ${ReactDOMServer.renderToString(<PaymentComplete drName={doctorName} amount={invoiceDetails?.invoiceTotal} invoiceUrl={receiptUrl} services={data?.service_data} />)}
            `;
            const providerBusinessEmail = data?.provider_id
                ? details?.[0]?.email
                : details?.email;
            const providerEmailContent = `
                <!DOCTYPE html>
                ${ReactDOMServer.renderToString(<PaymentComplete drName={data?.patient_name} amount={invoiceDetails?.invoiceTotal} invoiceUrl={receiptUrl} isProviderNotification={true} services={data?.service_data} />)}
            `;
            if (invoiceDetails?.patientEmail) {
                sendEmail(
                    token,
                    invoiceDetails?.patientEmail,
                    'Payment Successful',
                    ' ',
                    emailContent,
                    'Payment confirmation mail has been sent to email id',
                );
            }
            if (providerBusinessEmail) {
                sendEmail(
                    token,
                    providerBusinessEmail,
                    'Payment Received',
                    ' ',
                    providerEmailContent,
                    'Payment notification sent to provider/business',
                );
            }

            handleSuccess();
        } catch (e) {}
    };

    

    const makePayment = async () => {
        try {
            if (!paymentRef.customerId) return;
            setIsMakingPayment(true);
            
            if (!token) {
                console.error('No authentication token found');
                setIsMakingPayment(false);
                return;
            }

            if (!invoiceDetail?.id) {
                console.error('No invoice ID found');
                setIsMakingPayment(false);
                return;
            }

            const invoiceResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/invoice/${invoiceDetail.id}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!invoiceResponse.ok) {
                throw new Error('Failed to fetch invoice');
            }

            const invoiceResult = await invoiceResponse.json();
            // Backend may return { success, data } or the data directly
            const invData = invoiceResult?.success ?
                invoiceResult.data[0]:null;

            if (!invData) {
                throw new Error('Invoice not found');
            }

            const totalAmount = Math.round(Number(invData?.total || 0) );
            const { platformFee } = calculateFees(totalAmount, business?.fee_rate);
            const providerStripeId = business?.stripe_id || null;

            const createPaymentResponse = await fetch(`${process.env.REACT_APP_SERVER_URL}/create-payment-intent`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({
                  amount: totalAmount,
                  customerId: paymentRef?.customerId,
                  paymentMethodId: paymentRef?.paymentMethodId,
                  providerStripeId,
                  platformFee,
                  patientName: invData?.patient_name,
                  business_name: business?.name,
                  appointmentId: invData?.appointment_id,
                  provider_id: invData?.provider_id,
                  
                  serviceType: invData?.service_data?.map(service => service.name).join(", ") || "",
                }),
              });
            
              
            if (!createPaymentResponse.ok) {
                const error = await createPaymentResponse.json();
            console.error("Payment intent failed:", error);

            // Optional: show UI error
            setError(
                error?.error || "Payment failed. Please try again."
            );

            return;
            }
            const paymentIntentResp = await createPaymentResponse.json();
            // Saved-card (off-session) payments are confirmed server-side and the
            // backend returns the paymentIntentId directly. Manual card entry returns
            // a clientSecret instead. Support both shapes so payment_id is never null.
            const piData = paymentIntentResp?.data || {};
            const paymentIntentId =
                piData.paymentIntentId ||
                (piData.clientSecret ? piData.clientSecret.split('_secret_')[0] : null);

            if (!paymentIntentId) {
                console.error('No payment intent ID returned from create-payment-intent');
                throw new Error('Payment could not be confirmed. Please try again.');
            }

            if (!invoiceDetail?.id) {
                console.error('No invoice ID found');
                throw new Error('Invoice ID is required');
            }

            const invoiceUpdateResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/invoice/${invoiceDetail.id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        status: 'paid',
                        payment_id: paymentIntentId
                    })
                }
            );

            if (!invoiceUpdateResponse.ok) {
                const errorData = await invoiceUpdateResponse.json().catch(() => ({}));
                console.error('Error updating invoice:', errorData.error || 'Failed to update invoice');
                throw new Error(errorData.error || 'Failed to update invoice');
            }

            const invoiceUpdateResult = await invoiceUpdateResponse.json();
            
            // Handle response - could be object or array
            const data = invoiceUpdateResult.data[0];

            // Update appointment payment status and status to 'booked' if appointment_id exists
            if (data?.appointment_id) {
                const appointmentId = data.appointment_id;
                const markPaidResponse = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/appointments/${appointmentId}/mark-paid`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            payment_status: 'paid',
                            paid_date: new Date().toISOString(),
                            status: 'booked',
                            payment_id: paymentIntentId
                        })
                    }
                );

                if (!markPaidResponse.ok) {
                    const errorData = await markPaidResponse.json().catch(() => ({}));
                    console.error('Error marking appointment as paid:', errorData.error || 'Failed to mark appointment as paid');
                    throw new Error(errorData.error || 'Failed to mark appointment as paid');
                }
            }
            // Get receipt URL from Stripe. For Standard providers the charge lives on the
            // connected account, so scope the receipt lookup to that account.
            const receiptQuery2 = connectedAccountId ? `?stripeAccountId=${connectedAccountId}` : '';
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/v1/stripe/payment-receipt/${paymentIntentId}${receiptQuery2}`,
                {
                  method: "GET",
                  headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                  },
                }
              );
            
              if (!response.ok) {
                throw new Error("Failed to fetch receipt");
              }
            
              const { receiptUrl } = await response.json();
            const invoiceDetails = {
                patientEmail: data?.info_source_data,
                invoiceTotal: data?.total,
                providerId: data?.provider_id,
            };
            let details = null;
            if (invoiceDetails?.providerId) {
                // Fetch provider data via backend API
                if (!token) {
                    console.error('No authentication token found for provider fetch');
                    throw new Error('No authentication token found');
                }
                
                const providerResponse = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${invoiceDetails.providerId}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );
                
                if (!providerResponse.ok) {
                    const errorData = await providerResponse.json().catch(() => ({}));
                    console.error('Error fetching provider data:', errorData.error || 'Failed to fetch provider');
                    throw new Error(errorData.error || 'Failed to fetch provider data');
                }
                
                const providerResult = await providerResponse.json();
                
                // Handle response - convert to array format to match existing code expectations (details[0] is used later)
                const providerData = Array.isArray(providerResult.data) 
                    ? (providerResult.data.length > 0 ? providerResult.data[0] : null)
                    : (providerResult.data || null);
                
                if (!providerData) {
                    console.error('No provider data returned from API');
                    throw new Error('Provider data not found');
                }
                
                details = [providerData];
            } else {
                // Fetch business data via backend API
                if (!token) {
                    console.error('No authentication token found for business fetch');
                    throw new Error('No authentication token found');
                }
                
                if (!data?.business_id) {
                    console.error('No business ID found in invoice data');
                    throw new Error('Business ID not found');
                }

                const businessResponse = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/business/${data.business_id}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );
                
                if (!businessResponse.ok) {
                    const errorData = await businessResponse.json().catch(() => ({}));
                    console.error('Error fetching business data:', errorData.error || 'Failed to fetch business');
                    throw new Error(errorData.error || 'Failed to fetch business data');
                }
                
                const businessResult = await businessResponse.json();
                
                // Handle response - keep as object format to match existing code expectations (details.name, details.email)
                const businessData = Array.isArray(businessResult.data) 
                    ? (businessResult.data.length > 0 ? businessResult.data[0] : null)
                    : (businessResult.data || null);
                
                if (!businessData) {
                    console.error('No business data returned from API');
                    throw new Error('Business data not found');
                }
                
                details = businessData;
            }

            const doctorName = data?.provider_id
                ? `${details?.[0]?.first_name} ${details?.[0]?.last_name}`
                : details?.name;
            const emailContent = `
                <!DOCTYPE html>
                ${ReactDOMServer.renderToString(<PaymentComplete drName={doctorName} amount={invoiceDetails?.invoiceTotal} invoiceUrl={receiptUrl} services={data?.service_data} />)}
            `;
            const providerEmailContent = `
                <!DOCTYPE html>
                ${ReactDOMServer.renderToString(<PaymentComplete drName={data?.patient_name} amount={invoiceDetails?.invoiceTotal} invoiceUrl={receiptUrl} isProviderNotification={true} services={data?.service_data} />)}
            `;
            if (invoiceDetails?.patientEmail) {
                sendEmail(
                    token,
                    invoiceDetails?.patientEmail,
                    'Payment Successful',
                    ' ',
                    emailContent,
                    'Payment confirmation mail has been sent to email id',
                );
            }
            const providerBusinessEmail = data?.provider_id
                ? details?.[0]?.email
                : details?.email;
            if (providerBusinessEmail) {
                sendEmail(
                    token,
                    providerBusinessEmail,
                    'Payment Received',
                    ' ',
                    providerEmailContent,
                    'Payment notification sent to provider/business',
                );
            }

            handleSuccess();
        } catch (e) {
            // Swallowing this used to leave the popup open with no feedback after a
            // payment that had actually gone through.
            console.error('Error completing payment:', e);
            setError(e?.message || 'Payment could not be completed. Please try again.');
        } finally {
            setIsMakingPayment(false);
        }
    };

    const getBusinessServices = async () => {
        if (!business?.id || !token) return;
        try {
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/services`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ business_id: business.id })
                }
            );
            const result = await response.json();
            if (result.success && result.data) {
                const services = Array.isArray(result.data) ? result.data : [];
                setServicesList(services.map((service) => ({ name: service.name, price: service.price })));
            }
        } catch (error) {
            console.error('Error in getBusinessServices:', error);
        }
    };

    const getProviderServices = async (providerId) => {
        let providerServices = [];
        
        try {
            // Fetch provider services from backend API
            const { data: providerServicesData } = await getProviderServicesApi(providerId);
            providerServices = providerServicesData || [];

            // Filter for bookable services only
            providerServices = providerServices.filter(ps => ps.bookable === true);

            // Fetch service details for each provider service
            if (providerServices.length > 0) {
                const serviceIds = providerServices
                    .map(ps => ps.service_id)
                    .filter(id => id && id !== -1); // Filter out invalid service IDs

                if (serviceIds.length > 0) {
                    // Fetch services from backend API to get names and details
                    try {
                        // Get JWT token for authorization
                        if (!token) {
                            console.error('No authentication token found');
                            return;
                        }

                        if (!token) {
                            console.error('No authentication token found');
                        } else {
                            // Build comma-separated IDs string
                            // Fetch services from backend API
                            const response = await fetch(
                                `${process.env.REACT_APP_SERVER_URL}/api/providers/services`,
                                {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${token}`
                                    },
                                    body: JSON.stringify({ ids: serviceIds })
                                }
                            );

                            const result = await response.json();

                            if (!response.ok || result.error) {
                                const errorMessage = result.error || 'Failed to fetch services';
                                console.error('Error fetching services:', errorMessage);
                            } else {
                                // Backend returns data in result.data (should be an array)
                                const servicesData = Array.isArray(result.data) ? result.data : [];

                                if (servicesData && servicesData.length > 0) {
                                    // Create a map of service_id to service details
                                    const servicesMap = {};
                                    servicesData.forEach(service => {
                                        servicesMap[service.id] = service;
                                    });

                                    // Join provider services with service details
                                    providerServices = providerServices.map(ps => ({
                                        ...ps,
                                        services: servicesMap[ps.service_id] || null
                                    }));
                                }
                            }
                        }
                    } catch (fetchError) {
                        console.error('Error fetching services from backend:', fetchError);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching provider services:', error);
            return;
        }

        // Fetch all business services from backend API
        let businessServices = [];
        try {
            // Get JWT token for authorization
            if (!token) {
                console.error('No authentication token found');
                return;
            }

            if (token) {
                const businessServicesResponse = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/services`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ business_id: business?.id })
                    }
                );

                const businessServicesResult = await businessServicesResponse.json();

                if (businessServicesResponse.ok && businessServicesResult.success && businessServicesResult.data) {
                    businessServices = Array.isArray(businessServicesResult.data) ? businessServicesResult.data : [];
                }
            }
        } catch (error) {
            console.error('Error fetching business services in getProviderServices:', error);
        }

        // Map provider services
        const providerServicesList = (providerServices || [])
            .filter(ps => ps.services) // Filter out any null services
            .map((ps) => ({
                name: ps.services.name,
                price: ps.price !== null && ps.price !== undefined ? ps.price : ps.services.price,
                source: 'provider'
            }));

        // Map business services
        const businessServicesList = (businessServices || []).map((service) => ({
            name: service.name,
            price: service.price,
            source: 'business'
        }));

        // Merge both lists, removing duplicates (keep provider version if duplicate)
        const providerServiceNames = new Set(providerServicesList.map(s => s.name));
        const uniqueBusinessServices = businessServicesList.filter(
            bs => !providerServiceNames.has(bs.name)
        );

        // Combine provider services first, then unique business services
        const combinedServices = [...providerServicesList, ...uniqueBusinessServices];

        setServicesList(combinedServices.map(({ name, price }) => ({ name, price })));
    };
    const getInvoiceDetails = async () => {
        if (invoiceDetail?.id) {
            setIsViewMode(true);

            try {
                // Validate access token
                if (!token) {
                    console.error('No authentication token found');
                    return;
                }

                // Always fetch fresh data from backend API to ensure we have the latest updates
                const response = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/invoice/${invoiceDetail?.id}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );

                const result = await response.json();

                if (!response.ok || result.error) {
                    const errorMessage = result.error || 'Failed to fetch invoice';
                    console.error('Error fetching invoice:', errorMessage);
                    return;
                }

                if (!result.success || !result.data) {
                    console.error('Unexpected response format from invoice API');
                    return;
                }
               
                const invoice = result.data[0];

            // Store the invoice status (invoice is now a single object, not an array)
            setInvoiceStatus(invoice?.status || '');
            setInvoiceCreatedAt(invoice?.created_at || null);
            setInvoiceLastSentAt(invoice?.last_sent_at || invoice?.created_at || null);
            setUserAcceptedTerms(!!invoice?.user_accepted_terms);

            const { serviceName, servicePrice } = invoice?.service_data?.reduce(
                (acc, curr) => {
                    acc?.serviceName?.push(curr?.name);
                    acc?.servicePrice?.push(curr?.price);
                    return acc;
                },
                { serviceName: [], servicePrice: [] },
            );
            
            
            // Convert time back to HH:mm format for the select input
            const timeOfServiceValue = invoice?.time_of_service;
            let timeValue = '';
            if (timeOfServiceValue) {
                // Handle different time formats: "HH:MM:SS", "HH:MM", or Date object
                if (typeof timeOfServiceValue === 'string') {
                    // Extract HH:MM from various formats
                    const timeMatch = timeOfServiceValue.match(/(\d{1,2}):(\d{2})/);
                    if (timeMatch) {
                        const hours = timeMatch[1].padStart(2, '0');
                        const minutes = timeMatch[2];
                        timeValue = `${hours}:${minutes}`;
                    } else {
                        timeValue = timeOfServiceValue.substring(0, 5); // Fallback
                    }
                } else if (timeOfServiceValue instanceof Date) {
                    // If it's a Date object, format it
                    const hours = String(timeOfServiceValue.getHours()).padStart(2, '0');
                    const minutes = String(timeOfServiceValue.getMinutes()).padStart(2, '0');
                    timeValue = `${hours}:${minutes}`;
                } else {
                    timeValue = String(timeOfServiceValue);
                }
            }
            
            // Get email address - for email invoices it's in info_source_data, for SMS invoices it's also in info_source_data
            const emailAddress = invoice?.info_source_data || '';
            
            // Normalize date format to YYYY-MM-DD for date input
            // Handle timezone issues by ensuring we always use the date part without time conversion
            // Use the same approach as the list view which uses moment.js
            let dateOfServiceValue = invoice?.date_of_service;
            
            if (dateOfServiceValue) {
                // CRITICAL FIX: Extract the date part directly from the string BEFORE any parsing
                // This avoids any timezone conversion issues
                if (typeof dateOfServiceValue === 'string') {
                    // Extract just the YYYY-MM-DD part from any format
                    // Handle: "2025-11-15", "2025-11-15T00:00:00Z", "2025-11-15T00:00:00.000Z", etc.
                    const dateMatch = dateOfServiceValue.match(/^(\d{4}-\d{2}-\d{2})/);
                    if (dateMatch && dateMatch[1]) {
                        dateOfServiceValue = dateMatch[1];
                    } else {
                        // If regex doesn't match, try splitting
                        const datePart = dateOfServiceValue.split('T')[0].split(' ')[0];
                        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
                            dateOfServiceValue = datePart;
                        } else {
                            console.warn('Could not extract date from:', dateOfServiceValue);
                        }
                    }
                } else if (dateOfServiceValue instanceof Date) {
                    // For Date objects, we need to be very careful about timezone
                    // The date might have been shifted by timezone already
                    // Use UTC methods to get the date components as stored
                    const year = dateOfServiceValue.getUTCFullYear();
                    const month = String(dateOfServiceValue.getUTCMonth() + 1).padStart(2, '0');
                    const day = String(dateOfServiceValue.getUTCDate()).padStart(2, '0');
                    const utcDate = `${year}-${month}-${day}`;
                    const localYear = dateOfServiceValue.getFullYear();
                    const localMonth = String(dateOfServiceValue.getMonth() + 1).padStart(2, '0');
                    const localDay = String(dateOfServiceValue.getDate()).padStart(2, '0');
                    const localDate = `${localYear}-${localMonth}-${localDay}`;
                    dateOfServiceValue = utcDate;
                } else {
                    // Try to convert to string first, then extract
                    const dateStr = String(dateOfServiceValue);
                    const dateMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
                    if (dateMatch && dateMatch[1]) {
                        dateOfServiceValue = dateMatch[1];
                    }
                }
            }
            
            
            const invoiceData = {
                patientName: invoice?.patient_name,
                patient_id: invoice?.patient_id, // Preserve patient ID when viewing existing invoice
                patientEmail: invoice?.info_source === 'sms' ? invoice?.info_source_data : emailAddress,
                dateOfService: dateOfServiceValue || '',
                timeOfService: timeValue, // Store as "HH:MM" for the select input
                provider: invoice?.provider_id ? invoice?.provider_id : invoice?.business_id,
                sendInvoiceBy: 'email', // Always default to email
                sendInvoiceByInfo: emailAddress,
                serviceName,
                servicePrice: (servicePrice || []).map((price) => Number(price || 0).toFixed(2)),
                isBusiness: !invoice?.provider_id, // Set isBusiness flag
            };
            setFormData(invoiceData);
            setOriginalFormData(invoiceData); // Store original data for comparison
            
            // Update invoiceDetail atom with fresh data from database so it has latest values when reopened
            // Only update if the data has actually changed to avoid unnecessary re-renders
            if (invoice) {
                const freshInvoiceData = invoice;
                // Check if key fields have changed before updating to avoid infinite loops
                const hasChanged = 
                    invoiceDetail?.date_of_service !== freshInvoiceData.date_of_service ||
                    invoiceDetail?.time_of_service !== freshInvoiceData.time_of_service ||
                    invoiceDetail?.patient_name !== freshInvoiceData.patient_name ||
                    invoiceDetail?.last_sent_at !== freshInvoiceData.last_sent_at;
                
                if (hasChanged || !invoiceDetail?.id) {
                    setInvoiceDetail({ ...invoiceDetail, ...freshInvoiceData });
                }
            }
            
            // Load appropriate services based on provider or business
            if (invoice?.provider_id) {
                getProviderServices(invoice.provider_id);
            } else {
                getBusinessServices();
            }
            
            // Set the services count to match the actual number of services in the invoice
            setServicesCount(serviceName?.length || 1);
            
            // Reset edit tracking
            setHasFormBeenEdited(false);
            } catch (error) {
                console.error('Error in getInvoiceDetails:', error);
            }
        }
    };

    const fetchStripeCardDetails = async (
        customerId,
        preferredPaymentMethodId
      ) => {
        try {
            if (!token) {
                console.error('No authentication token found');
                return;
            }
          const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/stripe/card-details`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
              },
            
            body: JSON.stringify({
              customerId,
              preferredPaymentMethodId,
            }),
          });
      
          if (!response.ok) {
            return null;
          }
      
          return await response.json();
        } catch (e) {
          return null;
        }
      };
      

    

    const loadPatientCardDetails = async (patientId) => {
        try {
            setIsLoadingCardDetails(true);
            
            // Get JWT token for authorization
            if (!token) {
                console.error('No authentication token found');
                return;
            }

            if (!token) {
                console.error('No authentication token found');
                setCardForm({ cardNumber: '', expiryMonth: '', expiryYear: '', cvv: '' });
                return;
            }

            // Fetch payment details from backend API
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/payment_details`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ patient_id: patientId })
                }
            );

            const result = await response.json();

            if (!response.ok || result.error) {
                const errorMessage = result.error || 'Failed to fetch payment details';
                console.error('Error fetching payment details:', errorMessage);
                setCardForm({ cardNumber: '', expiryMonth: '', expiryYear: '', cvv: '' });
                return;
            }

            // Backend returns data in result.data (could be array or single object)
            const paymentDetails = result.data || [];
            const entry = Array.isArray(paymentDetails) && paymentDetails.length > 0 
                ? paymentDetails[0] 
                : (paymentDetails && typeof paymentDetails === 'object' ? paymentDetails : null);
            if (!entry || !entry.customer_id) {
                setCardForm({ cardNumber: '', expiryMonth: '', expiryYear: '', cvv: '' });
                return;
            }
            const card = await fetchStripeCardDetails(entry.customer_id, entry.payment_method);
            if (card) {
                setCardForm({
                    cardNumber: `**** **** **** ${card.last4 || ''}`,
                    expiryMonth: card.exp_month ? String(card.exp_month).padStart(2, '0') : '',
                    expiryYear: card.exp_year ? String(card.exp_year).slice(-2) : '',
                    cvv: '***'
                });
                setPaymentRef({ customerId: entry.customer_id || '', paymentMethodId: entry.payment_method || '' });
            } else {
                setCardForm({ cardNumber: '', expiryMonth: '', expiryYear: '', cvv: '' });
                setPaymentRef({ customerId: '', paymentMethodId: '' });
            }
        } catch (e) {
            setCardForm({ cardNumber: '', expiryMonth: '', expiryYear: '', cvv: '' });
            setPaymentRef({ customerId: '', paymentMethodId: '' });
        } finally {
            setIsLoadingCardDetails(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Trim and convert email-related fields to lowercase
        const processedValue = (name === 'sendInvoiceByInfo' || name === 'patientEmail') ? value.trim().toLowerCase() : value;
        const newFormData = { ...formData, [name]: processedValue };
        setFormData(newFormData);
        
        // Check if form has been edited (only for pending invoices in view mode)
        if (isViewMode && invoiceStatus !== 'paid' && originalFormData) {
            const isEdited = JSON.stringify(newFormData) !== JSON.stringify(originalFormData);
            setHasFormBeenEdited(isEdited);
        }
    };

    const sendNotification = async({ data }) => {
        try {
            // Handle both object and array cases - preserve structure
            const invoiceData = Array.isArray(data) ? (data.length > 0 ? data[0] : null) : data;
            
            if (!invoiceData || !invoiceData.uuid) {
                console.error('Invalid invoice data for sending notification');
                return;
            }
           
            let paymentLink = process.env.REACT_APP_PROVIDER_PORTAL + '/payment/' + invoiceData.uuid;
            const providerDetail = providers.find((provider) => provider.id === Number(formData.provider));
            const totalAmount = formData.servicePrice.reduce((acc, price) => parseFloat(acc) + parseFloat(cleanPrice(price)), 0);
            if (!token) {
                console.error('No authentication token found');
                return;
            }
            if (formData.sendInvoiceBy === 'text') {
                const doctorName = formData?.isBusiness ? (business?.name || '') : (providerDetail?.first_name || '');
                const services = formData.serviceName.join(', ');
                const message = `Payment request from ${doctorName}. Amount: $${Number(totalAmount).toFixed(2)}. Services: ${services}. Date: ${formData.dateOfService}. Pay here: ${paymentLink}`;
                sendSmsMessage({
                    to: formData.sendInvoiceByInfo,
                    message,
                    doctorName,
                    service: services,
                    totalDue: Number(totalAmount).toFixed(2),
                    date: formData.dateOfService,
                    email: formData.patientEmail,
                    link: paymentLink,
                }, 'Invoice Sent!');
            } else {
                const emailContent = `
        <!DOCTYPE html>
        ${ReactDOMServer.renderToString(
            <PaymentRequest
                url={paymentLink}
                amount={totalAmount}
                drName={formData?.isBusiness ? business?.name : providerDetail?.first_name}
                drImage={formData?.isBusiness ? business?.image_url : providerDetail?.image_url}
                service={formData.serviceName.join(', ')}
                date={formData.dateOfService}
                patientName={formData.patientName}
                businessId={business?.id}
            />,
        )}
    `;  
                // email to patient
                sendEmail(token,formData.sendInvoiceByInfo, 'Payment Request', ' ', emailContent, 'Invoice Sent!');
            }
        } catch (error) {
            console.error('Error sending notification:', error);
        }
    };

    const resendEmail = async () => {
        try {
            setIsResendingEmail(true);
            isUpdatingRef.current = true; // Set flag to prevent reload during update
            
            // Get authentication token
            if (!token) {
                console.error('No authentication token found');
                return;
            }
            
            if (!token) {
                console.error('No authentication token found');
                setIsResendingEmail(false);
                return;
            }
            if (!areServicesFromList()) {
                alert('One or more services are not in the list. Please select a service from the dropdown or add that new service first.');
                setIsResendingEmail(false);
                return;
            }
            
            // Update invoice with current form data if any changes were made
            const totalAmount = formData.servicePrice.reduce((acc, price) => parseFloat(acc) + parseFloat(cleanPrice(price)), 0);
            
            // Update the invoice in the database
            const currentTime = new Date().toISOString();
            // Normalize date format to ensure it's saved correctly (YYYY-MM-DD)
            // Use null instead of empty string if date is not provided
            // Extract just the date part to avoid timezone conversion issues
            let normalizedDate = null;
            if (formData.dateOfService) {
                
                // Handle different date formats that might come from the date picker
                let dateStr = String(formData.dateOfService).trim();
                
                // If it's already in YYYY-MM-DD format, use it directly
                if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                    normalizedDate = dateStr;
                } else {
                    // Try to extract YYYY-MM-DD from other formats
                    // Handle formats like "DD MMMM, YYYY" (e.g., "17 November, 2025")
                    const dateMatch = dateStr.match(/(\d{1,2})\s+(\w+),\s+(\d{4})/);
                    if (dateMatch) {
                        const day = dateMatch[1].padStart(2, '0');
                        const monthName = dateMatch[2];
                        const year = dateMatch[3];
                        // Convert month name to number
                        const monthMap = {
                            'January': '01', 'February': '02', 'March': '03', 'April': '04',
                            'May': '05', 'June': '06', 'July': '07', 'August': '08',
                            'September': '09', 'October': '10', 'November': '11', 'December': '12'
                        };
                        const month = monthMap[monthName];
                        if (month) {
                            normalizedDate = `${year}-${month}-${day}`;
                        }
                    } else {
                        // Try to parse as Date and extract YYYY-MM-DD
                        try {
                            const dateObj = new Date(dateStr);
                            if (!isNaN(dateObj.getTime())) {
                                // Use local date components to avoid timezone issues
                                const year = dateObj.getFullYear();
                                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                                const day = String(dateObj.getDate()).padStart(2, '0');
                                normalizedDate = `${year}-${month}-${day}`;
                            }
                        } catch (e) {
                            console.error('Error parsing date:', e);
                        }
                    }
                    
                    // Final validation
                    if (normalizedDate && !/^\d{4}-\d{2}-\d{2}$/.test(normalizedDate)) {
                        console.warn('Invalid date format after normalization:', normalizedDate);
                        normalizedDate = null;
                    }
                }
            }
            
            // Normalize time format to ensure it's saved correctly (HH:MM or HH:MM:SS)
            // Use null instead of empty string if time is not provided
            let normalizedTime = null;
            if (formData.timeOfService) {
                normalizedTime = formData.timeOfService.split(' ')[0];
            }
            
            // Ensure provider_id is a number or null
            const providerIdValue = !formData.isBusiness && formData?.provider 
                ? (typeof formData.provider === 'string' ? parseInt(formData.provider, 10) : formData.provider)
                : null;
            
            
            let updatedInvoice = null; // Declare variable for updated invoice
            
            // Update invoice via backend API
            const updatePayload = {
                patient_name: formData.patientName,
                patient_id: formData.patient_id,
                date_of_service: normalizedDate,
                time_of_service: normalizedTime,
                provider_id: providerIdValue,
                business_id: business?.id || null,
                info_source: formData.sendInvoiceBy === 'text' ? 'sms' : 'email',
                total: totalAmount,
                info_source_data: formData.sendInvoiceBy === 'text' ? formData.patientEmail : formData.sendInvoiceByInfo,
                phone_number: formData.sendInvoiceBy === 'text' ? formData.sendInvoiceByInfo : null,
                service_data: formData.serviceName.map((name, index) => ({
                    name: name,
                    price: parseFloat(cleanPrice(formData.servicePrice[index])),
                })),
                last_sent_at: currentTime, // Update last sent timestamp when resending
            };
            try {
                const updateResponse = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/invoice/${invoiceDetail?.id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(updatePayload)
                    }
                );

                const updateResult = await updateResponse.json();

                if (!updateResponse.ok || updateResult.error) {
                    const errorMessage = updateResult.error || 'Failed to update invoice';
                    console.error('Error updating invoice:', errorMessage);
                    alert('Failed to update invoice. Please check the console for details.');
                    return;
                }

                if (!updateResult.success || !updateResult.data) {
                    console.error('Unexpected response format from invoice API');
                    alert('Failed to update invoice. Unexpected response format.');
                    return;
                }

                // Backend returns { success: true, data: {...} } or { success: true, data: [...] }
                // Preserve object structure - do not convert to array
                const updateData = updateResult.data[0];
                updatedInvoice = Array.isArray(updateData) ? (updateData.length > 0 ? updateData[0] : null) : updateData;
                
            } catch (error) {
                console.error('Error updating invoice:', error);
                alert('Failed to update invoice. Please check the console for details.');
                return;
            }
            
            // Use the updated invoice data from the update result (or fetch as fallback)
            let invoiceData;
            if (!updatedInvoice) {
                // Fallback: Get the current invoice data to get the UUID via backend API
                try {
                    const fetchResponse = await fetch(
                        `${process.env.REACT_APP_SERVER_URL}/api/providers/invoice/${invoiceDetail?.id}`,
                        {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            }
                        }
                    );

                    const fetchResult = await fetchResponse.json();

                    if (!fetchResponse.ok || fetchResult.error || !fetchResult.success || !fetchResult.data) {
                        console.error('Error fetching invoice:', fetchResult.error || 'Failed to fetch invoice');
                        return;
                    }

                    // Preserve object structure - do not convert to array
                    const fetchData = fetchResult.data[0];
                    if (!fetchData) {
                        return;
                    }
                    invoiceData = Array.isArray(fetchData) ? (fetchData.length > 0 ? fetchData[0] : null) : fetchData;
                    if (!invoiceData) {
                        return;
                    }
                } catch (error) {
                    console.error('Error fetching invoice:', error);
                    return;
                }
            } else {
                invoiceData = updatedInvoice;
            }
            
            // Update invoiceDetail atom with fresh data from the update
            // BUT don't trigger a full reload - just update the atom silently
            // The form already has the correct data, and we don't want to reload and potentially lose the date
            setInvoiceDetail({ ...invoiceDetail, ...invoiceData });
            
            // IMPORTANT: Don't let the useEffect reload the form immediately after update
            // The form already has the correct data, and reloading might cause the date to be lost
            // We'll only reload when the user closes and reopens the invoice
            let paymentLink = process.env.REACT_APP_PROVIDER_PORTAL + '/payment/' + invoiceData.uuid;
            
            // Always send by email
            const providerDetail = providers.find((provider) => provider.id === Number(formData.provider));
            const emailContent = `
        <!DOCTYPE html>
        ${ReactDOMServer.renderToString(
            <PaymentRequest
                url={paymentLink}
                amount={totalAmount}
                drName={formData?.isBusiness ? business?.name : providerDetail?.first_name}
                drImage={formData?.isBusiness ? business?.image_url : providerDetail?.image_url}
                service={formData.serviceName.join(', ')}
                date={formData.dateOfService}
                patientName={formData.patientName}
                businessId={business?.id}
            />,
        )} 
    `;  
            //email to patient
            sendEmail(token,formData.sendInvoiceByInfo, 'Payment Request', ' ', emailContent, 'Invoice Resent!');
            
            // Update the last sent timestamp in state
            setInvoiceLastSentAt(currentTime);
            
            // Reset form edited state and update original form data after sending
            // Use the current formData (which has the updated date) as the new original
            setHasFormBeenEdited(false);
            setOriginalFormData({ ...formData });
            
            // Update the last sent timestamp in the invoiceDetail atom
            // Don't reload invoice details here to avoid changing the form while user is viewing it
            // The data will be fresh when they close and reopen
            
            // Refresh the invoice list to ensure table has latest data
            // Wait for it to complete so when user reopens, table has fresh data
            if (getInvoices) {
                await getInvoices();
            }
            
            // Clear the updating flag after a short delay to allow state to settle
            setTimeout(() => {
                isUpdatingRef.current = false;
            }, 100);
        } catch (error) {
            isUpdatingRef.current = false; // Clear flag on error
        } finally {
            setIsResendingEmail(false);
        }
    };


    const handleSubmit = async (e) => {
        try {
            e.preventDefault();

            // Validate form before submission
            if (!isFormValid()) return;
            if (!areServicesFromList()) {
                alert('One or more services are not in the list. Please select a service from the dropdown or add that new service first.');
                return;
            }

            setIsCreatingInvoice(true);

            
            let selectedProvider = providers.find((provider) => provider.id === parseInt(formData.provider));

            setProviderEmail(selectedProvider?.first_name + ' ' + selectedProvider?.last_name);

            // Look up patient_id by email if not already set
            let patientId = formData.patient_id;
            if (!patientId) {
                const emailToSearch = formData.sendInvoiceBy === 'text' ? formData.patientEmail : formData.sendInvoiceByInfo;
                if (emailToSearch && emailToSearch.includes('@')) {
                    try {
                        // Get JWT token for authorization
                        if (!token) {
                            console.error('No authentication token found');
                            return;
                        }

                        if (token) {
                            // Encode email for URL safety
                            // Fetch patient from backend API by email
                            const response = await fetch(
                                `${process.env.REACT_APP_SERVER_URL}/api/providers/patients`,
                                {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${token}`
                                    },
                                    body: JSON.stringify({ email: emailToSearch.toLowerCase().trim(), limit: 1 })
                                }
                            );

                            const result = await response.json();

                            if (!response.ok || result.error) {
                                // Patient not found, patientId remains null
                                console.error('Error fetching patient by email:', result.error || 'Patient not found');
                            } else {
                                // Backend returns data in result.data (should be an array)
                                const patientData = Array.isArray(result.data) ? result.data : [];
                                
                                if (patientData && patientData.length > 0) {
                                    patientId = patientData[0].id;
                                }
                            }
                        }
                    } catch (e) {
                        // Patient not found, patientId remains null
                        console.error('Error looking up patient by email:', e);
                    }
                }
            }
            if (!token) {
                console.error('No authentication token found');
                return;
            }

            if (!token) {
                console.error('No authentication token found');
                return;
            }

            // Prepare invoice data
            const invoicePayload = {
                provider_id: !formData.isBusiness ? formData?.provider : null,
                patient_name: formData.patientName,
                patient_id: patientId || null,
                date_of_service: formData.dateOfService,
                time_of_service: formData.timeOfService,
                info_source: formData.sendInvoiceBy === 'text' ? 'sms' : 'email',
                status: 'pending',
                business_id: business?.id,
                total: formData.servicePrice.reduce((acc, price) => parseFloat(acc) + parseFloat(cleanPrice(price)), 0),
                uuid: generateUUID(token),
                info_source_data: formData.sendInvoiceBy === 'text' ? formData.patientEmail : formData.sendInvoiceByInfo,
                phone_number: formData.sendInvoiceBy === 'text' ? formData.sendInvoiceByInfo : null,
                service_data: formData.serviceName.map((name, index) => {
                    return {
                        name: name,
                        price: parseFloat(cleanPrice(formData.servicePrice[index])),
                    };
                }),
                terms_version: 'v1.2',
                invoice_type: 'invoice',
            };

            // Create invoice via backend API
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/invoices`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(invoicePayload)
                }
            );

            const result = await response.json();

            if (!response.ok || result.error) {
                const errorMessage = result.error || 'Failed to create invoice';
                console.error('Error creating invoice:', errorMessage);
                return;
            }

            // Backend returns { success: true, data: [...] } or { success: true, data: {...} }
            if (!result.success || !result.data) {
                console.error('Unexpected response format from invoice API');
                return;
            }

            // Preserve object structure - do not convert to array
            const data = result.data;
            // Handle both object and array cases
            const createdInvoice = Array.isArray(data) ? (data.length > 0 ? data[0] : null) : data;

            if (createdInvoice) {
                const currentTime = new Date().toISOString();
                
                // Update invoice with last_sent_at timestamp when first sent via backend API
                try {
                    const updateResponse = await fetch(
                        `${process.env.REACT_APP_SERVER_URL}/api/providers/invoice/${createdInvoice.id}`,
                        {
                            method: 'PUT',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({ last_sent_at: currentTime })
                        }
                    );

                    if (!updateResponse.ok) {
                        const updateResult = await updateResponse.json();
                        console.error('Error updating invoice:', updateResult.error || 'Failed to update invoice');
                    }
                } catch (error) {
                    console.error('Error updating invoice:', error);
                }
                
                // Update state
                setInvoiceLastSentAt(currentTime);
            }

            // If opened via Manual Payment, immediately collect card and charge
            if (manualMode) {
                try {
                    const { stripe, elements } = stripeCtx || {};
                    if (!stripe || !elements || !isManualCardComplete) {
                        console.error('Manual payment: Missing requirements', { 
                            hasStripe: !!stripe, 
                            hasElements: !!elements, 
                            isManualCardComplete 
                        });
                        return;
                    }
                    // Handle both object and array cases - preserve structure
                    const createdInvoice = Array.isArray(data) ? (data.length > 0 ? data[0] : null) : data;
                    if (!createdInvoice) {
                        console.error('Manual payment: No invoice data found');
                        return;
                    }

                    // Do not open invoice view; we keep modal context and complete flow silently

                    const services = createdInvoice?.service_data || [];
                const totalAmount = services.reduce((sum, s) => sum + s.price, 0);
                const { platformFee } = calculateFees(totalAmount,business?.fee_rate);
                const providerStripeId = business?.stripe_id;
            // 1️⃣ Call your backend to create the checkout session
            const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/create-payment-intent`, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                  },
                body: JSON.stringify({
                    amount: totalAmount,
                    platformFee,
                    providerStripeId,
                    email: createdInvoice?.info_source_data || '',
                    patient_name:createdInvoice?.patient_name,
                    business_name:business?.name,
                    appointment_id:createdInvoice?.appointment_id,
                    provider_id:createdInvoice?.provider_id,
                    serviceType:createdInvoice?.service_data?.map(service => service.name).join(", "),
                  }),
            });
                    const intentPayload = await res.json().catch(() => ({}));
                    if (!res.ok) {
                        toasterNotification(
                            'error',
                            '',
                            intentPayload?.error ||
                                intentPayload?.message ||
                                'Could not start payment. Please try again.',
                        );
                        return;
                    }
                    const { clientSecret } = intentPayload?.data || {};
                    if (!clientSecret) {
                        toasterNotification('error', '', 'Could not start payment. Please try again.');
                        return;
                    }
            
                    const cardElement = elements.getElement(CardNumberElement);
                    const result = await stripe.confirmCardPayment(clientSecret, {
                        payment_method: { card: cardElement },
                    });

                    if (result.error) {
                        toasterNotification(
                            'error',
                            '',
                            result.error.message ||
                                result.paymentIntent?.last_payment_error?.message ||
                                'Your card was declined. Please try another payment method.',
                        );
                        return;
                    }

                    const pi = result.paymentIntent;
                    const okStatus =
                        pi?.status === 'succeeded' ||
                        pi?.status === 'processing' ||
                        pi?.status === 'requires_capture';
                    if (!pi?.id || !okStatus) {
                        toasterNotification(
                            'error',
                            '',
                            pi?.last_payment_error?.message ||
                                'Payment was not completed. Please check your card details and try again.',
                        );
                        return;
                    }

                    await completePaymentAndNotify(pi.id, createdInvoice.id);
                    return;
                } catch (err) {
                    toasterNotification('error', '', err?.message || 'Payment failed. Please try again.');
                    return;
                }
            } else {
                // Default behavior: send invoice notification
                sendNotification({ data });
                handleSuccess();
                return;
            }

 
 
        } catch (error) {
 
        } finally {
            setIsCreatingInvoice(false);
        }
    };

    const handleClose = () => {
        setIsViewMode(false);
        setFormData(formObject);
        setServicesCount(1);
        setIsCreatingInvoice(false);
        setIsResendingEmail(false);
        setInvoiceStatus('');
        setOriginalFormData(null);
        setHasFormBeenEdited(false);
        onClose();
    };
    const handleSuccess = () => {
        if (typeof getInvoices === 'function') {
            getInvoices().then(() => {
                onClose();
                setFormData(formObject);
                setServicesCount(0);
                setTimeout(() => {
                    setServicesCount(1);
                }, 2000);
            });
        } else {
            try {
                window.dispatchEvent(new Event('invoices:refresh'));
            } catch (e) {}
            onClose();
            setFormData(formObject);
            setServicesCount(0);
            setTimeout(() => {
                setServicesCount(1);
            }, 2000);
        }
    };


    useEffect(() => {
        try {
            // Don't reload if we're currently updating (to prevent clearing the form)
            if (isUpdatingRef.current) {
                return;
            }
            
            if (invoiceDetail?.id && open) {
                getInvoiceDetails();
            } else if (!invoiceDetail?.id) {
                setFormData(formObject);
                setIsViewMode(false);
                setServicesCount(1); // Reset to 1 service when creating new invoice
                setInvoiceStatus('');
                setUserAcceptedTerms(false);
                setCardForm({ cardNumber: '', expiryMonth: '', expiryYear: '', cvv: '' });
            } else {
            }
        } catch (error) {
            console.error('Error in useEffect for invoiceDetail:', error);
        }
    }, [invoiceDetail, open]);

    useEffect(() => {
        try {
            if (isViewMode && userAcceptedTerms && formData?.patient_id) {
                loadPatientCardDetails(formData.patient_id);
            }
        } catch (error) {

        }
    }, [isViewMode, userAcceptedTerms, formData?.patient_id]);

    const formatSentTimestamp = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
        const month = d.toLocaleDateString('en-US', { month: 'long' });
        const day = d.getDate();
        const year = d.getFullYear();
        const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        return `Sent ${weekday}, ${month} ${day}, ${year} @ ${time}`;
    };

    const getTitleText = () => {
        if (!isViewMode) return manualMode ? 'Create & Charge Payment' : 'Create Invoice';
        return formData.patientName ? `Invoice for ${formData.patientName}` : 'Invoice';
    };

    const isPaidInvoice = () => {
        const status = invoiceStatus || invoiceDetail?.status;
        return String(status || '').trim().toLowerCase() === 'paid';
    };

    const handleDeleteInvoice = () => {
        if (isPaidInvoice()) {
            alert('Paid invoices cannot be deleted.');
            return;
        }
        setDeleteConfirmOpen(true);
    };

    const performDeleteInvoice = async () => {
        try {
            if (isPaidInvoice()) {
                setDeleteConfirmOpen(false);
                setIsDeletingInvoice(false);
                alert('Paid invoices cannot be deleted.');
                return;
            }

            if (!token) {
                console.error('No authentication token found');
                return;
            }

            const userEmail = user?.email || '';
            const deletedAt = new Date().toISOString();

            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/invoice/${invoiceDetail?.id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        status: 'deleted',
                        isDeleted: true,
                        deletedBy: userEmail,
                        deletedAt
                    })
                }
            );

            const result = await response.json();

            if (!response.ok || result.error) {
                const errorMessage = result.error || 'Failed to delete invoice';
                console.error('Error deleting invoice:', errorMessage);
                setDeleteConfirmOpen(false);
                setIsDeletingInvoice(false);
                alert(response.status === 403 ? errorMessage : 'Failed to delete invoice. Please check the console for details.');
                return;
            }

            setDeleteConfirmOpen(false);
            setIsDeletingInvoice(false);
            if (getInvoices) {
                getInvoices();
            }
            onClose();
        } catch (error) {
            console.error('Error deleting invoice:', error);
            setDeleteConfirmOpen(false);
            setIsDeletingInvoice(false);
            alert('Failed to delete invoice. Please check the console for details.');
        }
    };

    const handleConfirmDelete = async () => {
        setIsDeletingInvoice(true);
        await performDeleteInvoice();
    };

    const updateInvoiceBeforePayment = async () => {
        try {
            // Calculate total from current form data
            const totalAmount = formData.servicePrice.reduce((acc, price) => parseFloat(acc) + parseFloat(cleanPrice(price)), 0);
            
            // Normalize date format
            let normalizedDate = null;
            if (formData.dateOfService) {
                let dateStr = String(formData.dateOfService).trim();
                if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                    normalizedDate = dateStr;
                } else {
                    const dateMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
                    if (dateMatch && dateMatch[1]) {
                        normalizedDate = dateMatch[1];
                    } else {
                        const datePart = dateStr.split('T')[0].split(' ')[0];
                        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
                            normalizedDate = datePart;
                        }
                    }
                }
            }
            
            // Normalize time format
            let normalizedTime = null;
            if (formData.timeOfService) {
                normalizedTime = formData.timeOfService.split(' ')[0];
            }
            
            // Ensure provider_id is a number or null
            const providerIdValue = !formData.isBusiness && formData?.provider 
                ? (typeof formData.provider === 'string' ? parseInt(formData.provider, 10) : formData.provider)
                : null;
            
            // Update the invoice in the database with current form data via backend API
            if (!token) {
                console.error('No authentication token found for invoice update');
                alert('Failed to update invoice. Authentication error.');
                return false;
            }
            
            if (!invoiceDetail?.id) {
                console.error('No invoice ID found');
                alert('Failed to update invoice. Invoice ID not found.');
                return false;
            }
            if (!areServicesFromList()) {
                alert('One or more services are not in the list. Please select a service from the dropdown or add that new service first.');
                return false;
            }
            
            const updatePayload = {
                patient_name: formData.patientName,
                patient_id: formData.patient_id,
                date_of_service: normalizedDate,
                time_of_service: normalizedTime,
                provider_id: providerIdValue,
                business_id: business?.id || null,
                info_source: formData.sendInvoiceBy === 'text' ? 'sms' : 'email',
                total: totalAmount,
                info_source_data: formData.sendInvoiceBy === 'text' ? formData.patientEmail : formData.sendInvoiceByInfo,
                phone_number: formData.sendInvoiceBy === 'text' ? formData.sendInvoiceByInfo : null,
                service_data: formData.serviceName.map((name, index) => ({
                    name: name,
                    price: parseFloat(cleanPrice(formData.servicePrice[index])),
                })),
            };
            const updateResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/invoice/${invoiceDetail.id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(updatePayload)
                }
            );
            
            const apiResponse = await updateResponse.json();
            
            if (!updateResponse.ok || apiResponse.error) {
                const errorMessage = apiResponse.error || 'Failed to update invoice';
                console.error('Error updating invoice before payment:', errorMessage);
                alert('Failed to update invoice. Please try again.');
                return false;
            }
            
            // Backend returns { success: true, data: {...} } or { success: true, data: [...] }
            // Convert to Supabase-like format to match existing code expectations (updateResult.data[0] is used later)
            const updateResult = {
                error: null,
                data: null
            };
            
            if (apiResponse.success && apiResponse.data) {
                // Ensure data is an array to match Supabase format
                updateResult.data = Array.isArray(apiResponse.data) 
                    ? apiResponse.data
                    : [apiResponse.data];
            } else {
                console.error('Unexpected response format from invoice update API');
                alert('Failed to update invoice. Unexpected response format.');
                return false;
            }
            
            // Update invoiceDetail atom with fresh data
            if (updateResult.data && updateResult.data.length > 0) {
                setInvoiceDetail({ ...invoiceDetail, ...updateResult.data[0] });
            }
            
            return true;
        } catch (error) {
            console.error('Error updating invoice before payment:', error);
            alert('Failed to update invoice. Please try again.');
            return false;
        }
    };

    const handleEnterManualPayment = async () => {
        // Update invoice with current form data before opening payment modal
        const updated = await updateInvoiceBeforePayment();
        if (updated) {
            setIsManualPaymentOpen(true);
            setManualPaymentAuthorized(false);
        }
    };

    const getStickyFooter = () => {
        if (!isViewMode) {
            return (
                <StickyActionFooter
                    onCancel={onClose}
                    onSave={handleSubmit}
                    cancelText="Cancel"
                    saveText={manualMode ? 'Create & Charge' : 'Create Invoice'}
                    isLoading={isCreatingInvoice}
                    disabled={!isFormValid() || (manualMode ? (!isManualCardComplete || !manualCreateAuthorized || isCreatingInvoice) : isCreatingInvoice)}
                />
            );
        }

        if (isViewMode && !userAcceptedTerms && invoiceStatus !== 'paid') {
            return (
                <StickyActionFooter
                    onCancel={handleEnterManualPayment}
                    onSave={resendEmail}
                    cancelText="Enter Manual Payment"
                    saveText={isResendingEmail ? <ButtonLoader /> : (hasFormBeenEdited ? 'Send Updated Invoice' : 'Resend Invoice')}
                    isLoading={isResendingEmail}
                    disabled={false}
                    showDelete={true}
                    onDelete={handleDeleteInvoice}
                    deleteText="Delete Invoice"
                />
            );
        }

        if (isViewMode && userAcceptedTerms && invoiceStatus !== 'paid') {
            return null;
        }

        if (isViewMode) {
            return (
                <StickyActionFooter
                    onCancel={onClose}
                    onSave={null}
                    cancelText="Close"
                    saveText=""
                    isLoading={false}
                    disabled={true}
                    // Paid invoices are a financial record — deleting one would drop it
                    // from the books even though the money has already moved.
                    showDelete={invoiceStatus !== 'paid'}
                    onDelete={handleDeleteInvoice}
                    deleteText="Delete Invoice"
                />
            );
        }

        return null;
    };

    return (
        <>
        <FormModal
            open={open}
            onClose={handleClose}
            title={getTitleText()}
            subText={
                isViewMode && (invoiceLastSentAt || invoiceCreatedAt)
                    ? `${formatSentTimestamp(invoiceLastSentAt || invoiceCreatedAt)}. You can resend the original invoice, edit the details and send an updated version to the patient, or enter the payment details manually if the patient prefers.`
                    : !isViewMode
                    ? manualMode
                        ? 'Create an invoice and immediately process payment with the patient\'s card details. This allows you to collect payment on the spot without waiting for the patient to pay online.'
                        : 'Create and send invoices to collect payments from your patients. Fill out the patient details and services below to generate a professional invoice.'
                    : ''
            }
            badge={isViewMode && invoiceStatus ? <StatusBadge status={invoiceStatus} /> : null}
            stickyFooter={getStickyFooter()}
        >
            <FormStyle onSubmit={handleSubmit}>
                <Grid container spacing={0}>
                    <Grid item className={'mb-20 mt-20'} xs={12}>
                        <FormHeading>Patient Details</FormHeading>
                    </Grid>

                    {(() => {
                        const cachedProviderItem = !formData.isBusiness && formData.provider
                            ? providerFullDetails.find(item => String((item.provider ?? item).id) === String(formData.provider))
                            : null;
                        // Business selected → empty arrays skip FormTextInput's API calls entirely
                        // Provider in cache → use cached data; provider not in cache → undefined triggers fallback fetch
                        const invoiceOooData = formData.isBusiness ? []
                            : (cachedProviderItem ? (cachedProviderItem.ooo ?? []) : undefined);
                        const invoiceAvailabilityData = formData.isBusiness ? []
                            : (cachedProviderItem ? (cachedProviderItem.availability ?? []) : undefined);
                        return (
                            <Form
                                formData={formData}
                                setFormData={setFormData}
                                handleChange={handleChange}
                                providers={providers}
                                openProviderDropdown={openProviderDropdown}
                                setOpenProviderDropdown={setOpenProviderDropdown}
                                servicesCount={servicesCount}
                                setServicesCount={setServicesCount}
                                invoice={true}
                                servicesList={servicesList}
                                isViewMode={invoiceStatus === 'paid'}
                                business={business}
                                getProviderServices={getProviderServices}
                                getBusinessServices={getBusinessServices}
                                oooData={invoiceOooData}
                                providerAvailabilityData={invoiceAvailabilityData}
                                onAddNewServiceClick={!isViewMode ? (index) => {
                                    setCurrentServiceIndex(index);
                                    setOpenServicePopup(true);
                                } : undefined}
                            />
                        );
                    })()}

                    {!isViewMode && (
                        <>
                            {manualMode ? (
                                <>
                                    <Grid item xs={12} className={'mt-f-22'}>
                                        <FormHeading>Card Details</FormHeading>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Elements stripe={stripePromise}>
                                            <InlineCardElements disabled={false} onCompleteChange={setIsManualCardComplete} />
                                            <StripeContextBridge onReady={setStripeCtx} />
                                        </Elements>
                                    </Grid>
                                    <Grid item xs={12} className={'mt-f-22'}>
                                        <div className="text-base font-bold text-gray-800 mb-2">By checking the box and tapping pay, I hereby:</div>
                                        <div className="w-full h-px mb-4" style={{ backgroundColor: 'rgba(196, 196, 196, 1)' }}></div>
                                        <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                                            <div className="flex items-start gap-3">
                                                <div
                                                    role="checkbox"
                                                    aria-checked={manualCreateAuthorized}
                                                    tabIndex={0}
                                                    onClick={handleManualAuthorizationToggle}
                                                    onKeyDown={handleManualAuthorizationKeyDown}
                                                    style={{
                                                        width: 16,
                                                        height: 16,
                                                        borderRadius: '4px',
                                                        border: manualCreateAuthorized ? '1px solid #75BD2F' : '1px solid #cbc1b7',
                                                        backgroundColor: manualCreateAuthorized ? '#75BD2F' : '#f5f1ed',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        marginTop: '2px',
                                                        transition: 'all 0.3s ease',
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    {manualCreateAuthorized && (
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white" stroke="white" strokeWidth="1.5" />
                                                        </svg>
                                                    )}
                                                </div>
                                                <span
                                                    className="text-sm text-gray-600 leading-relaxed cursor-pointer"
                                                    onClick={handleManualAuthorizationToggle}
                                                >
                                                    Confirm patient authorization to enter card details for this invoice.
                                                </span>
                                            </div>
                                        </div>
                                    </Grid>
                                </>
                            ) : null}
                        </>
                    )}

                    {isViewMode && !userAcceptedTerms && invoiceStatus !== 'paid' && null}

                    {isViewMode && userAcceptedTerms && invoiceStatus !== 'paid' && (
                        <>
                            <Grid item xs={12} className={'mt-f-22'}>
                                <FormHeading>Card Details</FormHeading>
                            </Grid>
                            <Grid item xs={12}>
                                {cardForm.cardNumber && cardForm.cardNumber.startsWith('****') && !isEditingSavedCard ? (
                                    <div className="grid grid-cols-1 gap-4">
                                        <div className="text-sm text-gray-600">Saved card: {cardForm.cardNumber} (exp **/**)</div>
                                        <StickyActionFooter
                                            showDelete={invoiceStatus === 'pending'}
                                            onDelete={handleDeleteInvoice}
                                            deleteText="Delete Invoice"
                                            onCancel={() => setIsEditingSavedCard(true)}
                                            cancelText="Edit"
                                            onSave={makePayment}
                                            saveText="Make Payment"
                                            isLoading={isMakingPayment}
                                            disabled={!paymentRef.customerId}
                                            className="border-0"
                                            style={{
                                                backgroundColor: 'transparent',
                                                backdropFilter: 'none',
                                                WebkitBackdropFilter: 'none',
                                                borderTop: '0',
                                                padding: '0',
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <Elements stripe={stripePromise}>
                                        <CardElementsAndPayButton
                                            invoiceId={invoiceDetail?.id}
                                            isMakingPayment={isMakingPayment}
                                            setIsMakingPayment={setIsMakingPayment}
                                            onPaymentConfirmed={async (intentId) => {
                                                await completePaymentAndNotify(intentId);
                                                setIsEditingSavedCard(false);
                                            }}
                                            payLabel={'Pay'}
                                            savedCardData={cardForm.cardNumber && cardForm.cardNumber.startsWith('****') ? cardForm : null}
                                            showDelete={invoiceStatus === 'pending'}
                                            onDelete={handleDeleteInvoice}
                                            deleteText="Delete Invoice"
                                        />
                                    </Elements>
                                )}
                            </Grid>
                        </>
                    )}
                </Grid>
            </FormStyle>
        </FormModal>

        {isManualPaymentOpen ? (
            <ManualPaymentModal
                open={isManualPaymentOpen}
                onClose={() => setIsManualPaymentOpen(false)}
                invoiceDetail={invoiceDetail}
                formData={formData}
                isMakingPayment={isMakingPayment}
                setIsMakingPayment={setIsMakingPayment}
                manualPaymentAuthorized={manualPaymentAuthorized}
                setManualPaymentAuthorized={setManualPaymentAuthorized}
                onPaymentConfirmed={async (paymentIntentId) => {
                    await completePaymentAndNotify(paymentIntentId);
                    setIsManualPaymentOpen(false);
                }}
                completePaymentAndNotify={completePaymentAndNotify}
                cleanPrice={cleanPrice}
            />
        ) : null}

        <ServicePopup
            open={openServicePopup}
            onClose={() => {
                setOpenServicePopup(false);
                setCurrentServiceIndex(null);
            }}
            businessId={business?.id}
            getServiceList={() => getBusinessServices()}
            onServiceAdded={(newService) => {
                if (currentServiceIndex === null || currentServiceIndex === undefined || !newService) return;
                const price = newService.price != null ? String(Number(newService.price).toFixed(2)) : '0.00';
                setFormData((prev) => {
                    const newServiceNames = [...(prev.serviceName || [])];
                    const newServicePrices = [...(prev.servicePrice || [])];
                    if (!newServiceNames[currentServiceIndex]) newServiceNames[currentServiceIndex] = '';
                    if (!newServicePrices[currentServiceIndex]) newServicePrices[currentServiceIndex] = '';
                    newServiceNames[currentServiceIndex] = newService.name || newService.service || '';
                    newServicePrices[currentServiceIndex] = price;
                    return { ...prev, serviceName: newServiceNames, servicePrice: newServicePrices };
                });
                setOpenServicePopup(false);
                setCurrentServiceIndex(null);
            }}
        />

        <ConfirmDeleteInvoiceModal
            open={deleteConfirmOpen}
            onClose={() => setDeleteConfirmOpen(false)}
            onConfirm={handleConfirmDelete}
            isLoading={isDeletingInvoice}
        />
        </>
    );
};

export default InvoicePopup;
